import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SkillBadges = () => {
  const [animatedSkills, setAnimatedSkills] = useState([]);

  const skillCategories = [
    {
      category: "Frontend",
      icon: "Monitor",
      color: "electric-cyan",
      skills: [
        { name: "React", level: 100 },
        { name: "JavaScript", level: 100 },
      
        { name: "Tailwind CSS", level: 100 },
        { name: "HTML5/CSS3", level: 100 }
      ]
    },
    {
      category: "Backend",
      icon: "Server",
      color: "trust-green",
      skills: [
        { name: "Node.js", level: 100 },
        { name: "Express.js", level:100  },
       
        { name: "MongoDB", level:100  },
        
        { name: "REST APIs", level: 100 }
      ]
    },
    {
      category: "Tools & DevOps",
      icon: "Settings",
      color: "rocket-fire",
      skills: [
        { name: "Git/GitHub", level: 100 },
        { name: "Vercel", level: 100 },
        { name: "VS Code", level: 100 },
        { name: "Postman", level: 100 },
        { name: "MongoDB Compass", level: 100 },
        { name: "Vite", level: 100 }
      ]
    }
  ];

  const floatingSkills = [
    { name: "React", x: 10, y: 20, delay: 0 },
    { name: "Node.js", x: 80, y: 15, delay: 1000 },
    { name: "MongoDB", x: 15, y: 70, delay: 2000 },
    { name: "TypeScript", x: 75, y: 80, delay: 1500 },
    { name: "AWS", x: 50, y: 10, delay: 500 },
    { name: "Docker", x: 85, y: 50, delay: 2500 }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedSkills(floatingSkills);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const getSkillColor = (level) => {
    if (level >= 90) return 'bg-trust-green';
    if (level >= 80) return 'bg-electric-cyan';
    if (level >= 70) return 'bg-rocket-fire';
    return 'bg-slate-400';
  };

  const getCategoryColor = (color) => {
    switch (color) {
      case 'electric-cyan': return 'from-electric-cyan to-electric-cyan/70';
      case 'trust-green': return 'from-trust-green to-trust-green/70';
      case 'rocket-fire': return 'from-rocket-fire to-rocket-fire/70';
      default: return 'from-slate-500 to-slate-400';
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-deep-space via-slate-900 to-slate-800 bg-background relative overflow-hidden">
      {/* Floating Background Skills */}
      <div id="SkillsAndTech" className="absolute inset-0 pointer-events-none">
        {animatedSkills?.map((skill, index) => (
          <div
            key={index}
            className="absolute animate-float"
            style={{
              left: `${skill?.x}%`,
              top: `${skill?.y}%`,
              animationDelay: `${skill?.delay}ms`,
              animationDuration: '6s'
            }}
          >
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1 text-xs text-slate-400">
              {skill?.name}
            </div>
          </div>
        ))}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-surface rounded-full px-4 py-2 mb-4">
            <Icon name="Zap" size={16} className="text-rocket-fire" />
            <span className="text-deep-space text-sm font-medium">Technical Arsenal</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-headline text-deep-space mb-4  text-white">
            Skills & Technologies
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            A comprehensive toolkit built through hands-on experience, 
            continuous learning, and real-world project implementation.
          </p>
        </div>

        {/* Skills Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {skillCategories?.map((category, categoryIndex) => (
            <div
              key={categoryIndex}
              className="bg-white rounded-2xl shadow-brand p-6 border border-border hover:shadow-brand-lg transition-brand"
            >
              {/* Category Header */}
              <div className="flex items-center space-x-3 mb-6">
                <div className={`w-12 h-12 bg-gradient-to-br ${getCategoryColor(category?.color)} rounded-xl flex items-center justify-center shadow-brand`}>
                  <Icon name={category?.icon} size={24} color="white" />
                </div>
                <div>
                  <h3 className="text-xl font-value-prop text-deep-space">{category?.category}</h3>
                  <p className="text-sm text-text-secondary">{category?.skills?.length} technologies</p>
                </div>
              </div>

              {/* Skills List */}
              <div className="space-y-4">
                {category?.skills?.map((skill, skillIndex) => (
                  <div key={skillIndex} className="group flex flex-col gap-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-deep-space group-hover:text-rocket-fire transition-brand">
                        {skill?.name}
                      </span>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-text-secondary">{skill?.experience}</span>
                        
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-brand-surface rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full ${getSkillColor(skill?.level)} rounded-full transition-all duration-1000 ease-out`}
                        style={{ width: `${skill?.level}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              
            </div>
          ))}
        </div>

        
      </div>
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(5px) rotate(-1deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default SkillBadges;